import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";

import { useToast } from "../../../components/Toast";
import { Button } from "../../../components/ui";
import {
  actualizarEmpresaConfiguracion,
  type EmpresaConfiguracion,
  type EmpresaConfiguracionUpdate,
} from "../api";

/**
 * Hook compartido por todas las sub-tabs de Configuración.
 *
 * Centraliza el patrón:
 *   - Form local derivado del cfg actual.
 *   - Sincronización al cambiar el cfg externo.
 *   - Cálculo del diff (sólo se envían campos cambiados).
 *   - Mutation con toast + actualización del cache de TanStack Query.
 */
export function useConfigForm<Form>(
  cfg: EmpresaConfiguracion,
  extractor: (cfg: EmpresaConfiguracion) => Form,
  differ: (form: Form, cfg: EmpresaConfiguracion) => EmpresaConfiguracionUpdate,
): {
  form: Form;
  setForm: React.Dispatch<React.SetStateAction<Form>>;
  cambios: EmpresaConfiguracionUpdate;
  haycambios: boolean;
  pending: boolean;
  reset: () => void;
  save: () => void;
} {
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(() => extractor(cfg));

  useEffect(() => setForm(extractor(cfg)), [cfg, extractor]);

  const mut = useMutation({
    mutationFn: (payload: EmpresaConfiguracionUpdate) =>
      actualizarEmpresaConfiguracion(payload),
    onSuccess: (data) => {
      qc.setQueryData(["empresa", "configuracion"], data);
      toast.success("Cambios guardados");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "No se pudo actualizar");
    },
  });

  const cambios = differ(form, cfg);
  const haycambios = Object.keys(cambios).length > 0;

  return {
    form,
    setForm,
    cambios,
    haycambios,
    pending: mut.isPending,
    reset: () => setForm(extractor(cfg)),
    save: () => mut.mutate(cambios),
  };
}

/** Helper para normalizar string vacío → null antes de comparar. */
export function nullIfEmpty(s: string | null | undefined): string | null {
  if (s == null) return null;
  const t = s.trim();
  return t === "" ? null : t;
}

export function SaveBar({
  haycambios,
  pending,
  reset,
  save,
}: {
  haycambios: boolean;
  pending: boolean;
  reset: () => void;
  save: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-end gap-2 border-t border-neutral-200 pt-4">
      <Button
        variant="secondary"
        onClick={reset}
        disabled={!haycambios || pending}
      >
        Restablecer
      </Button>
      <Button
        icon={Save}
        onClick={save}
        disabled={!haycambios || pending}
      >
        {pending ? "Guardando…" : "Guardar"}
      </Button>
    </div>
  );
}
